// MaturaCosineSideClosedTask.js
// Zadanie maturalne – zamknięte
// Długość boku BC z twierdzenia cosinusów

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import TriangleTwoSidesAngleDiagram from "../../../../components/diagrams/TriangleTwoSidesAngleDiagram";

const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

// format długości BC: liczba całkowita albo sqrt(n)
function formatLengthLatex(n) {
  if (n <= 0) n = 1;
  const root = Math.sqrt(n);
  if (Number.isInteger(root)) {
    return `${root}`;
  }
  return `\\sqrt{${n}}`;
}

function createOriginalTask() {
  const optionLatex = ["\\sqrt{28}", "\\sqrt{40}", "8", "\\sqrt{76}"];
  const options = optionLatex.map((latex, index) => ({
    latex,
    label: ["A", "B", "C", "D"][index],
    isCorrect: index === 0,
  }));

  return {
    type: "MaturaCosineSideClosedTask",
    data: {
      AB: 6,
      AC: 4,
      angleA: 60,
      BC2: 28,
      options,
      correctIndex: 0,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: "22.2",
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  // Wariant podstawowy zgodny z dotychczasowymi zadaniami CKE: kąt 60°.
  const angleA = 60;

  const AB = rnd(5, 10);
  const AC = rnd(4, 9);

  // twierdzenie cosinusów: BC^2 = AB^2 + AC^2 - 2AB*AC*cosA
  const cosA = 0.5;

  const BC2 = AB * AB + AC * AC - 2 * AB * AC * cosA;
  const BC2Int = Math.round(BC2); // powinno być całkowite po tych wyborach

  const correctLatex = formatLengthLatex(BC2Int);

  // generujemy „sąsiednie” wartości pod pierwiastkiem
  const candidates = new Set();
  candidates.add(BC2Int);

  const base = AB * AB + AC * AC;

  const maybeAdd = (val) => {
    const v = Math.round(val);
    if (v > 0) candidates.add(v);
  };

  maybeAdd(base); // bez części -/+ AB*AC
  maybeAdd(BC2Int + AB * AC);
  maybeAdd(BC2Int - AB * AC);
  maybeAdd(BC2Int + 4);
  maybeAdd(BC2Int + 9);

  const allVals = Array.from(candidates).slice(0, 4);

  // jeśli z jakiegoś powodu mniej niż 4, dodosyp trochę liczb
  let extra = 2;
  while (allVals.length < 4) {
    const val = BC2Int + extra * extra;
    if (!allVals.includes(val)) allVals.push(val);
    extra++;
  }

  // budujemy opcje A–D
  const letters = ["A", "B", "C", "D"];

  const rawOptions = allVals.map((v) => {
    const latex = formatLengthLatex(v);
    return {
      latex,
      isCorrect: v === BC2Int,
    };
  });

  // tasowanie
  const shuffled = [...rawOptions];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const options = shuffled.map((opt, idx) => ({
    ...opt,
    label: letters[idx],
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaCosineSideClosedTask",
    data: {
      AB,
      AC,
      angleA,
      BC2: BC2Int,
      options,
      correctIndex,
    },
  };
}

// === render task ===

export function renderTask({ data }) {
  const { AB, AC, angleA, options } = data;
  const diagramSize = angleA > 90 ? 480 : 320;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Dany jest trójkąt <InlineMath math={'ABC'} />, w którym{" "}
        <InlineMath math={`|AB| = ${AB}`} />,{" "}
        <InlineMath math={`|AC| = ${AC}`} /> oraz{" "}
        <InlineMath math={`\\left|\\angle CAB\\right| = ${angleA}^{\\circ}`} />.
        (Zobacz rysunek).
      </p>

      <div
        style={{
          marginTop: 4,
          marginBottom: 10,
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <TriangleTwoSidesAngleDiagram
          size={diagramSize}
          angleDeg={angleA}
          sideAB={AB}
          sideAC={AC}
        />
      </div>

      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Długość boku <InlineMath math={'BC'} /> trójkąta <InlineMath math={'ABC'} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// === input (ABCD) ===

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// === check ===

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return false;
  }
  const { correctIndex } = data;
  return userAnswer.choiceIndex === correctIndex;
}

// === answer (tylko literka + treść wariantu) ===

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

// === user answer ===

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// === explanation (Z KROKAMI) ===

export function renderExplanation({ data }) {
  const { AB, AC, angleA, BC2 } = data;

  const cosLatex =
    angleA === 60
      ? "\\cos 60^{\\circ} = \\tfrac{1}{2}"
      : "\\cos 120^{\\circ} = -\\tfrac{1}{2}";

  // po wstawieniu cos: -2ABAC cos(A) daje odpowiednio -ABAC (dla 60) albo +ABAC (dla 120)
  const sign = angleA === 60 ? "-" : "+";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Zapisujemy twierdzenie cosinusów dla boku{" "}
        <InlineMath math={'BC'} /> (naprzeciwko kąta <InlineMath math={'\\angle CAB'} />
        ):
      </p>

      <BlockMath math={'|BC|^{2} = |AB|^{2} + |AC|^{2} - 2|AB|\\,|AC|\\cos \\angle CAB'} />

      <p>
        <strong>Krok 2.</strong> Podstawiamy dane z zadania:
      </p>

      <BlockMath
        math={`|BC|^{2} = ${AB}^{2} + ${AC}^{2} - 2\\cdot ${AB}\\cdot ${AC}\\cos ${angleA}^{\\circ}`}
      />

      <p>
        <strong>Krok 3.</strong> Wstawiamy wartość cosinusa:
      </p>

      <BlockMath math={cosLatex} />

      <p>
        <strong>Krok 4.</strong> Podstawiamy do wzoru i obliczamy:
      </p>

      <BlockMath
        math={`|BC|^{2} = ${AB}^{2} + ${AC}^{2} - 2\\cdot ${AB}\\cdot ${AC}\\cdot \\cos ${angleA}^{\\circ}`}
      />

      <BlockMath
        math={
          angleA === 60
            ? `|BC|^{2} = ${AB}^{2} + ${AC}^{2} - 2\\cdot ${AB}\\cdot ${AC}\\cdot \\left(\\tfrac{1}{2}\\right)`
            : `|BC|^{2} = ${AB}^{2} + ${AC}^{2} - 2\\cdot ${AB}\\cdot ${AC}\\cdot \\left(-\\tfrac{1}{2}\\right)`
        }
      />

      <BlockMath math={`|BC|^{2} = ${AB}^{2} + ${AC}^{2} ${sign} ${AB}\\cdot ${AC}`} />

      <BlockMath math={`|BC|^{2} = ${BC2}`} />
      <BlockMath math={`|BC| = ${formatLengthLatex(BC2)}`} />
    </div>
  );
}
